import { DocumentNode, gql } from '@apollo/client';
import { HStack, Link, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import {
  MembershipTermLifecycleStatus,
  MembershipTermPaymentStatus,
} from '@/graphql-types/globalTypes';
import { BriefcaseIcon, CreditCardIcon } from '@/imports/ui/chakra/feather';
import { READABLE_PAYMENT_STATUS } from '@/utilities';

import {
  MembershipTermDetailsHeader_MembershipTermDataFragment,
  MembershipTermDetailsHeader_MembershipTermDataFragment_parentMembershipTerm,
} from './graphql-types/MembershipTermDetailsHeader_MembershipTermDataFragment';

import { AccordionMembershipCard_MembershipDataFragment_relevantMembershipTerms_membershipType } from '../../graphql-types/AccordionMembershipCard_MembershipDataFragment';
import { RenewalSettingSelect } from './RenewalSettingSelect';

export const MembershipTermDetailsHeader: React.FC<{
  membershipTerm: MembershipTermDetailsHeader_MembershipTermDataFragment;
  userId?: string;
  organizationId?: string;
  shouldBeAdminView: boolean;
  canCurrentUserManageOrganizationProfile: boolean;
  membershipType: AccordionMembershipCard_MembershipDataFragment_relevantMembershipTerms_membershipType;
}> & { membershipTermDataFragment: DocumentNode } = ({
  membershipTerm,
  userId,
  organizationId,
  shouldBeAdminView,
  canCurrentUserManageOrganizationProfile,
  membershipType,
}) => {
  const hasParentMembershipTermOwner = !!(
    membershipTerm.parentMembershipTerm?.owner.__typename === 'Organization' &&
    membershipTerm.parentMembershipTerm.owner
  );

  if (hasParentMembershipTermOwner) {
    return (
      <InheritedTermHeader
        parentMembershipTerm={membershipTerm.parentMembershipTerm}
        shouldBeAdminView={shouldBeAdminView}
        canCurrentUserManageOrganizationProfile={
          canCurrentUserManageOrganizationProfile
        }
      />
    );
  } else if (
    membershipTerm.canCurrentUserChangeRenewal ||
    membershipTerm.paymentStatus
  ) {
    return (
      <HStack spacing="4">
        {membershipTerm.paymentStatus &&
          membershipTerm.lifecycleStatus !==
            MembershipTermLifecycleStatus.EXPIRED && (
            <PaymentStatusTag
              paymentStatus={membershipTerm.paymentStatus}
              paymentsRemaining={membershipTerm.paymentsRemaining}
            />
          )}

        {membershipTerm.canCurrentUserChangeRenewal && (
          <RenewalSettingSelect
            membershipTerm={membershipTerm}
            userId={userId}
            organizationId={organizationId}
            disabled={
              membershipType.membershipRenewalSettings.renewalPeriodInDays <= 0
            }
          />
        )}
      </HStack>
    );
  } else {
    return null;
  }
};

const PaymentStatusTag: React.FC<{
  paymentStatus: MembershipTermPaymentStatus;
  paymentsRemaining: number | null;
}> = ({ paymentStatus, paymentsRemaining }) => {
  return (
    <HStack spacing="2" color="gray.500">
      <CreditCardIcon />
      {paymentStatus ===
      MembershipTermPaymentStatus.MONTHLY_PAYMENTS_REMAINING ? (
        <PaymentsRemainingLabel paymentsRemaining={paymentsRemaining} />
      ) : (
        <Text textStyle="detailStrong" textTransform="uppercase">
          {READABLE_PAYMENT_STATUS[paymentStatus]}
        </Text>
      )}
    </HStack>
  );
};

const PaymentsRemainingLabel: React.FC<{
  paymentsRemaining: number | null;
}> = ({ paymentsRemaining }) => {
  return (
    <Text textStyle="detailStrong" textTransform="uppercase">
      {paymentsRemaining === 1
        ? '1 Monthly payment remaining'
        : `${paymentsRemaining} monthly payments remaining`}
    </Text>
  );
};

const InheritedTermHeader: React.FC<{
  parentMembershipTerm: MembershipTermDetailsHeader_MembershipTermDataFragment_parentMembershipTerm | null;
  shouldBeAdminView: boolean;
  canCurrentUserManageOrganizationProfile: boolean;
}> = ({
  parentMembershipTerm,
  shouldBeAdminView,
  canCurrentUserManageOrganizationProfile,
}) => {
  if (!parentMembershipTerm) {
    return null;
  }

  return (
    <HStack color="gray.500">
      <BriefcaseIcon />
      <Text textStyle="detailStrong" textTransform="uppercase">
        {parentMembershipTerm.lifecycleStatus ===
        MembershipTermLifecycleStatus.IMPENDING
          ? 'WILL INHERIT FROM '
          : parentMembershipTerm.lifecycleStatus ===
            MembershipTermLifecycleStatus.EXPIRED
          ? 'INHERITED FROM '
          : 'INHERITS FROM '}
        <Link
          as={NextLink}
          href={
            shouldBeAdminView
              ? `/admin/companies/${
                  parentMembershipTerm.owner.__typename === 'Organization' &&
                  parentMembershipTerm.owner._id
                }/details`
              : canCurrentUserManageOrganizationProfile
              ? '/my-company/dashboard/overview'
              : `/companies/${
                  parentMembershipTerm.owner.__typename === 'Organization' &&
                  parentMembershipTerm.owner._id
                }`
          }
        >
          {parentMembershipTerm.owner.__typename === 'Organization' &&
            parentMembershipTerm.owner.displayName}
        </Link>
      </Text>
    </HStack>
  );
};

MembershipTermDetailsHeader.membershipTermDataFragment = gql`
  fragment MembershipTermDetailsHeader_MembershipTermDataFragment on MembershipTermV2 {
    _id
    lifecycleStatus
    paymentsRemaining
    canCurrentUserChangeRenewal
    paymentStatus
    parentMembershipTerm {
      _id
      startDate
      lifecycleStatus
      owner {
        ... on Organization {
          _id
          displayName
        }
      }
    }
    ...RenewalSettingSelect_MembershipTermDataFragment
  }
  ${RenewalSettingSelect.membershipTermDataFragment}
`;
